// Slideshow.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import Twemoji from 'react-twemoji';
import SlideContent from '../slideContent/Slidecontent';
import CircularLogo from './logo.png';
import ProgressTracker from '../progressTracker/ProgressTracker';
import './Slideshow.css';

function Slideshow() {
  const { userId } = useParams();
  const slidesCount = 30; // Valid indices: 0 to 28

  const [slidesData, setSlidesData] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Base backgrounds for every slide
  const backgrounds = [
    'linear-gradient(to right, #dd3e54, #6be585)',       // Slide 0
    '#123456', 
    'url("https://picsum.photos/seed/slide1/1600/900") center/cover no-repeat',
    'radial-gradient(circle, #ff0000, #0000ff)',        // Slide 3
    '#FFC300',                                          // Slide 4
    '#FF5733',                                          // Slide 5
    '#C70039',                                          // Slide 6
    '#900C3F',                                          // Slide 7
    '#581845',                                          // Slide 8
    'linear-gradient(135deg, #12c2e9, #c471ed, #f7797d)', // Slide 9
    'linear-gradient(135deg, #12c2e9, #c471ed, #f7797d)', // Slide 10
    '#2ECC71',                                          // Slide 11
    '#8E44AD',                                          // Slide 12
    '#8E44AD',                                          // Slide 13
    '#F4D03F',                                          // Slide 14
    'url("https://picsum.photos/seed/slide1/1600/900") center/cover no-repeat', // Slide 15
    '#123456',                                          // Slide 16
    'radial-gradient(circle, #ff0000, #0000ff)',         // Slide 17
    '#FFC300',                                          // Slide 18
    '#FF5733',                                          // Slide 19
    '#C70039',                                          // Slide 20
    '#900C3F',                                          // Slide 21
    '#581845',                                          // Slide 22
    'radial-gradient(circle, #ff0000, #0000ff)',         // Slide 23
    'radial-gradient(circle, #ff0000, #0000ff)',         // Slide 24 (base; emoji overlay will be added)
    'radial-gradient(circle, #ff0000, #0000ff)',         // Slide 25
    'radial-gradient(circle, #ff0000, #0000ff)',         // Slide 26
    'radial-gradient(circle, #ff0000, #0000ff)',         // Slide 27
    '#581845',                                          // Slide 28 (human-friendly slide 29)
    'linear-gradient(to right, #dd3e54, #6be585)'
  ];
  const backgroundStyle = backgrounds[currentSlide] ?? 'transparent';

  // (Optional) Indices for other overlays (collage overlay, for example)
  const collageSlideIndices = [0, 4, 14, 28];

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await fetch(
          `https://jpf8ds43gf.execute-api.us-west-2.amazonaws.com/prod/data?userId=${userId}`,
          { method: 'GET' }
        );
        const data = await response.json();
        setSlidesData(data);
      } catch (error) {
        console.error('Error fetching slides:', error);
      }
    };
    fetchSlides();
  }, [userId]);

  // Use functional updates for stable state management
  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, slidesCount - 1));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      action();
    }
  };

  // Add a global keydown listener for Arrow keys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        prevSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // The empty dependency array is safe here due to our functional updates

  //Allow tiktok style sliding
  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;
  
    const handleTouchStart = (e) => {
      touchStartY = e.changedTouches[0].clientY;
    };
  
    const handleTouchEnd = (e) => {
      touchEndY = e.changedTouches[0].clientY;
      handleSwipeGesture();
    };
  
    const handleSwipeGesture = () => {
      const swipeThreshold = 150; // Minimum distance in px to be considered a swipe
      const deltaY = touchStartY - touchEndY;
  
      if (Math.abs(deltaY) > swipeThreshold) {
        if (deltaY > 0) {
          nextSlide(); // Swipe up → next
        } else {
          prevSlide(); // Swipe down → previous
        }
      }
    };
  
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
  
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Extract the top emojis from your data (if available)
  const topEmojis = slidesData?.wrappedData?.Item?.top_emojis;
  const emojiKeys = topEmojis ? Object.keys(topEmojis) : [];

  return (
    <div
      className="slideshow-container"
      style={{ background: backgroundStyle }}
    >
      <ProgressTracker slideIndex={currentSlide} totalSlides={slidesCount} />
      
      {/* The slide-wrapper is relative so overlays can be absolutely positioned */}
      <div className="slide-wrapper" style={{ position: 'relative' }}>
        {/* Existing collage overlay for certain slides */}
        {collageSlideIndices.includes(currentSlide) && slidesData?.wrappedData?.Item && (
          <div className="collage-background">
            {[
              slidesData.wrappedData.Item.randomImage1,
              slidesData.wrappedData.Item.randomImage2,
              slidesData.wrappedData.Item.randomImage3,
              slidesData.wrappedData.Item.randomImage4,
              slidesData.wrappedData.Item.randomImage5,
              slidesData.wrappedData.Item.randomImage6,
              slidesData.wrappedData.Item.randomImage7,
              slidesData.wrappedData.Item.randomImage8,
              slidesData.wrappedData.Item.randomImage9,
            ]
              .filter(Boolean)
              .map((src, idx) => (
                <img key={idx} src={src} alt={`collage-${idx}`} className="collage-image" />
              ))}
          </div>
        )}

        {/* Render the emoji overlay on slide 20 */}
        {currentSlide === 20 && emojiKeys.length > 0 && (
          <div className="emoji-background">
            {[...Array(50)].map((_, i) => {
              const randomEmoji = emojiKeys[Math.floor(Math.random() * emojiKeys.length)];
              const style = {
                position: 'absolute',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                fontSize: `${Math.random() * 2 + 1}rem`, // Random size between 1rem and 3rem
                transform: `rotate(${Math.random() * 360}deg)`,
                opacity: 0.2,
              };
              return (
                <span key={i} style={style}>
                  <Twemoji options={{ className: 'twemoji' }}>
                    {randomEmoji}
                  </Twemoji>
                </span>
              );
            })}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className={`slide ${collageSlideIndices.includes(currentSlide) ? 'full-slide' : ''}`}
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.8 }}
            transition={{ duration: 0.5 }}
          >
            <SlideContent data={slidesData} slideIndex={currentSlide} />
          </motion.div>
        </AnimatePresence>
      </div>

      {currentSlide > 0 && (
        <div
          className="arrow-wrapper up-arrow-wrapper"
          onClick={prevSlide}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => handleKeyPress(e, prevSlide)}
          aria-label="Previous Slide"
        >
          <FaArrowUp className="up-arrow" />
        </div>
      )}

      {currentSlide < slidesCount - 1 && (
        <div
          className="arrow-wrapper down-arrow-wrapper"
          onClick={nextSlide}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => handleKeyPress(e, nextSlide)}
          aria-label="Next Slide"
        >
          <FaArrowDown className="down-arrow" />
        </div>
      )}

      {/* Circular logo with clickable redirect (opens in new tab) */}
      <div className="logo-container">
        <a href="https://sizzlewrap.com" target="_blank" rel="noopener noreferrer">
          <img src={CircularLogo} alt="Logo" className="circular-logo" />
        </a>
      </div>
    </div>
  );
}

export default Slideshow;
